import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  model,
  output,
  Signal,
  TemplateRef,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { MenuItem } from '../menu/menu-item';
import { MenuComponent, MenuDirective } from '../menu/menu.directive';
import {
  CORE_PROFILE_USER,
  ProfileComponent,
  User,
} from '../profile/profile.component';
import { NavigationBarComponent } from './navigation-bar.component';
import { OrgProductLogoComponent } from './org-product-logo.component';

@Component({
  selector: 'core-nav-header',
  imports: [
    CommonModule,
    RouterModule,
    MatMenuModule,
    ButtonModule,
    NavigationBarComponent,
    ProfileComponent,
    OrgProductLogoComponent,
  ],
  template: `
    <div class="border-b border-b-accent" data-testid="header">
      <div
        class="flex h-16 w-full max-content-width flex-row items-center mx-auto"
      >
        <div class="h:full flex flex-row items-center md:hidden">
          <p-button
            #popoverTrigger="matMenuTrigger"
            [matMenuTriggerFor]="popover"
            (window:resize)="popoverTrigger.closeMenu()"
            data-testid="menu"
            [text]="true"
            [plain]="true"
          >
            <span class="material-icons text-primary scale-125 transform"
              >menu</span
            >
          </p-button>
        </div>
        <core-org-product-logo [orgLogo]="resolvedOrgLogo()" class="m-4 h-8" />
        <core-nav-bar class="hidden md:block" [menu]="menu()" />
        <div class="ml-auto mr-4 hidden md:flex items-center">
          @if (leadingActionsTemplate(); as leadingActionsTemplate) {
            <ng-container
              [ngTemplateOutlet]="leadingActionsTemplate"
            ></ng-container>
          }
          <ng-container *ngTemplateOutlet="profile"></ng-container>
          @if (trailingActionsTemplate(); as trailingActionsTemplate) {
            <ng-container
              [ngTemplateOutlet]="trailingActionsTemplate"
            ></ng-container>
          }
        </div>
        <mat-menu #popover="matMenu" class="bg-1">
          <ng-container *ngTemplateOutlet="profile"></ng-container>
          @for (item of menu(); track item.routerLink) {
            <button
              [routerLink]="item.routerLink"
              mat-menu-item
              routerLinkActive="pop-active"
            >
              <span class="text-primary">{{ item.label }}</span>
            </button>
          }
        </mat-menu>
        <ng-template #profile>
          <core-profile
            (login)="onLogin()"
            (logout)="onLogout()"
            (switchCustomer)="onSwitchCustomer()"
          />
        </ng-template>
      </div>
    </div>
  `,
  styles: [
    `
      .pop-active {
        border-left: solid 3px red;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [MenuDirective],
  providers: [
    {
      provide: MenuComponent,
      useExisting: forwardRef(() => NavigationHeaderComponent),
    },
  ],
})
export class NavigationHeaderComponent extends MenuComponent {
  public override readonly menu = model<MenuItem[]>([]);

  public readonly leadingActionsTemplate = input<TemplateRef<unknown> | null>(
    null,
  );

  public readonly trailingActionsTemplate = input<TemplateRef<unknown> | null>(
    null,
  );

  private readonly injectedUser =
    inject<Signal<User | undefined>>(CORE_PROFILE_USER);
  protected readonly resolvedOrgLogo = computed(
    () => this.injectedUser()?.organization?.logoUrl,
  );

  public login = output<void>();

  public logout = output<void>();

  public switchCustomer = output<void>();

  protected onLogin() {
    this.login.emit();
  }

  protected onLogout() {
    this.logout.emit();
  }

  protected onSwitchCustomer() {
    this.switchCustomer.emit();
  }
}
